import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Plus, ClipboardList, Trash2, Search, ArrowUpDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { getAuditType } from "@/lib/auditTypes";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export default function AuditsList() {
  const navigate = useNavigate();
  const [audits, setAudits] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [frameworkFilter, setFrameworkFilter] = useState("all");
  const [sortBy, setSortBy] = useState("updated_desc");

  const load = () => api.get("/audits").then((r) => setAudits(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try { await api.delete(`/audits/${id}`); toast.success("Audit eliminato"); load(); }
    catch { toast.error("Errore eliminazione"); }
  };

  const openAudit = (event, id) => {
    if (event.target.closest("button, a")) return;
    navigate(`/app/audit/${id}`);
  };

  const frameworks = Array.from(new Set(audits.flatMap((audit) => (audit.types || [audit.type]).filter(Boolean))));
  const visibleAudits = audits
    .filter((audit) => {
      const query = search.trim().toLowerCase();
      const types = audit.types || [audit.type];
      const matchesSearch = !query || [audit.title, audit.company_name].filter(Boolean).some((value) => value.toLowerCase().includes(query));
      return matchesSearch && (statusFilter === "all" || audit.status === statusFilter) && (frameworkFilter === "all" || types.includes(frameworkFilter));
    })
    .sort((a, b) => {
      if (sortBy === "score_desc") return (b.score || 0) - (a.score || 0);
      if (sortBy === "score_asc") return (a.score || 0) - (b.score || 0);
      if (sortBy === "company_asc") return (a.company_name || "").localeCompare(b.company_name || "", "it", { sensitivity: "base" });
      if (sortBy === "title_asc") return (a.title || "").localeCompare(b.title || "", "it", { sensitivity: "base" });
      return (b.updated_at || "").localeCompare(a.updated_at || "");
    });

  return (
    <div className="p-6 md:p-10 max-w-6xl">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Compliance</div>
          <h1 className="font-display font-black text-4xl md:text-5xl">Audit</h1>
          <p className="text-slate-500 mt-2 text-sm">Tutti gli audit GDPR e NIS2 svolti.</p>
        </div>
        <Link to="/audit/nuovo" data-testid="new-audit-btn-list">
          <Button className="h-11 bg-slate-900 hover:bg-slate-800 px-5"><Plus className="h-4 w-4" /> Nuovo Audit</Button>
        </Link>
      </div>

      {audits.length > 0 && <div className="mb-6 flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white p-3">
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <Input data-testid="audit-filter-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca per titolo o azienda..." className="h-10 pl-9" />
        </div>
        <select data-testid="audit-filter-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-700">
          <option value="all">Tutti gli stati</option>
          <option value="in_progress">In corso</option>
          <option value="completed">Completati</option>
        </select>
        <select data-testid="audit-filter-framework" value={frameworkFilter} onChange={(e) => setFrameworkFilter(e.target.value)} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-700">
          <option value="all">Tutti i framework</option>
          {frameworks.map((framework) => <option key={framework} value={framework}>{getAuditType(framework).label}</option>)}
        </select>
        <div className="flex items-center gap-2">
          <ArrowUpDown className="h-4 w-4 text-slate-400" />
          <select data-testid="audit-sort" value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-700">
            <option value="updated_desc">Più recenti</option>
            <option value="score_desc">Score più alto</option>
            <option value="score_asc">Score più basso</option>
            <option value="company_asc">Azienda A-Z</option>
            <option value="title_asc">Titolo A-Z</option>
          </select>
        </div>
        {(search || statusFilter !== "all" || frameworkFilter !== "all") && <button type="button" onClick={() => { setSearch(""); setStatusFilter("all"); setFrameworkFilter("all"); }} className="h-10 px-2 text-xs font-medium text-slate-500 hover:text-slate-900">Azzera filtri</button>}
      </div>}

      {audits.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg p-16 text-center">
          <ClipboardList className="h-12 w-12 text-slate-300 mx-auto mb-4" strokeWidth={1.5} />
          <div className="font-display font-bold text-lg mb-2">Nessun audit</div>
          <p className="text-slate-500 text-sm mb-6">Crea il tuo primo audit selezionando una checklist.</p>
          <Link to="/audit/nuovo"><Button className="bg-slate-900 hover:bg-slate-800"><Plus className="h-4 w-4" /> Nuovo Audit</Button></Link>
        </div>
      ) : visibleAudits.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg p-12 text-center text-sm text-slate-500">Nessun audit corrisponde ai filtri.</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                <th className="px-5 py-3">Tipo</th>
                <th className="px-5 py-3">Titolo</th>
                <th className="px-5 py-3">Azienda</th>
                <th className="px-5 py-3">Score</th>
                <th className="px-5 py-3">Stato</th>
                <th className="px-5 py-3">Aggiornato</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visibleAudits.map((a) => (
                <tr key={a.id} data-testid={`audit-row-${a.id}`} onClick={(event) => openAudit(event, a.id)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); openAudit(event, a.id); } }} tabIndex={0} className="cursor-pointer hover:bg-slate-50 focus-visible:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 transition-colors duration-200">
                  <td className="px-5 py-4">
                    <div className="flex flex-wrap gap-1">
                      {(a.types || [a.type]).filter(Boolean).map((t) => {
                        const meta = getAuditType(t);
                        const Icon = meta.icon;
                        return (
                          <div key={t} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${meta.badge_bg} ${meta.badge_text}`}>
                            <Icon className="h-2.5 w-2.5" />
                            {meta.label}
                          </div>
                        );
                      })}
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <Link to={`/audit/${a.id}`} className="font-medium text-slate-900 hover:text-blue-600 transition-colors duration-200" data-testid={`audit-title-${a.id}`}>{a.title}</Link>
                    <div className="text-[10px] text-slate-400 mt-0.5 font-mono-plex">v{a.version || 1}{a.frozen ? " · Storico" : ""}</div>
                  </td>
                  <td className="px-5 py-4 text-sm text-slate-600">{a.company_name}</td>
                  <td className="px-5 py-4 font-mono-plex font-bold text-slate-900">{a.score}%</td>
                  <td className="px-5 py-4">
                    <span className={`text-[11px] uppercase tracking-wider font-semibold ${a.status === "completed" ? "text-emerald-600" : "text-amber-600"}`}>
                      {a.status === "completed" ? "Completato" : "In corso"}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-xs font-mono-plex text-slate-500">{a.updated_at?.slice(0,16).replace("T"," ")}</td>
                  <td className="px-5 py-4 text-right">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button className="p-2 text-slate-400 hover:text-red-600 rounded-md transition-colors duration-200" data-testid={`delete-audit-${a.id}`}><Trash2 className="h-4 w-4" /></button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Eliminare l'audit?</AlertDialogTitle>
                          <AlertDialogDescription>Operazione irreversibile.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Annulla</AlertDialogCancel>
                          <AlertDialogAction onClick={() => remove(a.id)} className="bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
